/**
 * @voltxampere/engine — Newton-Raphson DC iteration
 *
 * SPECS §3.3 pseudo-code, transcribed:
 *
 *   x ← x0
 *   for iter = 0 .. maxIter:
 *     (Y, rhs) ← buildMNA(circuit, t=0, xPrev=x)
 *     x_new ← luSolve(Y, rhs)
 *     if isConverged(x_new - x, x_new): return x_new
 *     x ← x_new
 *   throw ConvergenceError
 *
 * The implementation reuses Sprint 4's primitives:
 *   - `sparseSolve` from `@voltxampere/math` for the dense LU pass
 *     (SPECS §3.11 says dense is acceptable below ~1000 unknowns;
 *     Sprint 5 circuits sit well under that),
 *   - `isConverged` for the SPECS §3.4 predicate.
 *
 * NaN/Inf guard: every solve result is sniff-checked for finite
 * entries before the convergence test. If LU produces a non-finite
 * iterate we abort with `ConvergenceError` rather than letting
 * a poisoned vector pollute the next build.
 *
 * Linear circuits converge in 1 iteration (the convergence test
 * fires on the *second* build, but the first solve has the right
 * answer). The loop intentionally does the second build to confirm.
 *
 * Sprint 8c additions:
 *   - Accepts an optional `initialJunctionHistory` from the caller
 *     (`DCSolver.solve` populates this via {@link ./initial-conditions.ts}
 *     `applyInitialConditions`). The history is cloned on entry so
 *     the caller's map stays immutable.
 *   - Runs Armijo backtracking on the reduced-KCL residual
 *     ({@link ./line-search.ts}) when the Armijo gate is open:
 *     either (a) the caller supplied initial conditions, or (b)
 *     {@link StallDetector} fires after ≥3 stalled iterations.
 *   - Records an `armijoEvents` counter on the result so
 *     `/ultrareview` and tests can distinguish paths.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ConvergenceError, ContractViolationError } from "@voltxampere/shared";
import { sparseSolve, isFiniteVector } from "@voltxampere/math";
import { buildMNA } from "./mna-builder.js";
import { applyDeltaVDamping } from "./delta-v-damping.js";
import { REFERENCE_TEMPERATURE_K, vtAtTemperature, } from "../stamps/nonlinear/physical-constants.js";
import { computeResidual, isConvergedReduced } from "./residual.js";
import { armijoLineSearch, DEFAULT_ARMIJO_OPTIONS } from "./line-search.js";
import { DEFAULT_STALL_OPTIONS, StallDetector } from "./stall-detection.js";
/**
 * Shallow-clone the pnjlim junction history for Armijo probing.
 *
 * The clone is SHALLOW because the value type is a primitive
 * number. {@link JunctionHistoryEntry} is a compile-time guard:
 * if a future sprint widens the entry shape, TypeScript forces
 * a review here — a structured clone or per-field copy would be
 * needed so Armijo probes cannot mutate sub-objects visible to
 * the main driver.
 */
export function snapshotJunctionHistory(src) {
    return new Map(src);
}
export function newtonRaphson(circuit, plan, opts) {
    if (!Number.isInteger(opts.maxIterations) || opts.maxIterations <= 0) {
        throw new ContractViolationError(`newtonRaphson: maxIterations must be a positive integer, got ${String(opts.maxIterations)}`);
    }
    if (opts.x0.length !== plan.size) {
        throw new ContractViolationError(`newtonRaphson: x0 length ${String(opts.x0.length)} ≠ N+M ${String(plan.size)}`);
    }
    if (plan.size === 0) {
        // Degenerate but valid: a circuit with only ground (no nets to
        // solve) trivially converges. Return an empty vector.
        const empty = new Float64Array(0);
        return {
            converged: true,
            iterations: 0,
            x: empty,
            clampEvents: 0,
            finalResidualNorm: 0,
            armijoEvents: 0,
        };
    }
    let x = new Float64Array(opts.x0);
    let iterations = 0;
    let clampEvents = 0;
    let armijoEvents = 0;
    let finalResidualNorm = 0;
    let previousStepDelta = null;
    const tier = opts.tier ?? "strict";
    // Sprint 7 pnjlim: a single junction history shared across all
    // Newton iterations of this solve. Stamps read the previous iter's
    // limited voltage and write back the new one. Gmin / source-step
    // continuations each get their own solver call (and therefore their
    // own fresh map) — correct behaviour: pnjlim tracks continuity
    // within one solve, not across continuations where the solution
    // can legitimately jump.
    //
    // Sprint 8c: the caller may seed the map via
    // `initialJunctionHistory`. We clone (not adopt) so the caller's
    // map remains immutable from this driver's perspective.
    const junctionHistory = new Map(opts.initialJunctionHistory ?? []);
    // Sprint 8a.1: previous iter's junction deltas, consumed by the
    // damping helper to detect sign-flip oscillation (the true
    // "engage damping" signal). `null` on the first iter.
    let previousDeltas = null;
    const dampingEnabled = opts.enableDeltaVDamping ?? true;
    const armijoEnabled = (opts.enableArmijo ?? true) && plan.junctionBearingIndices.size > 0;
    const hasIC = opts.hasInitialConditions ?? false;
    const armijoOptions = opts.armijoOptions ?? DEFAULT_ARMIJO_OPTIONS;
    // Sprint 80.Z L-AYARLAR-5 — runtime temperature → trust-region size.
    const temperatureK = opts.temperature ?? REFERENCE_TEMPERATURE_K;
    const vt = vtAtTemperature(temperatureK);
    const deltaVLimit = 10 * vt;
    // Sprint 8c: stall detector is always instantiated (cheap) but
    // only consulted when armijoEnabled. Reset per-solve so gmin /
    // source continuations don't inherit a stale window.
    const stallDetector = new StallDetector(DEFAULT_STALL_OPTIONS);
    // When initial conditions were supplied, Armijo fires from iter 1.
    // Otherwise it lies dormant until stall detection trips.
    let armijoActive = armijoEnabled && hasIC;
    /**
     * Sandboxed residual evaluator for Armijo backtracking. Builds
     * MNA at the trial iterate with a CLONED junctionHistory so
     * pnjlim's mutations don't pollute the main loop's continuity
     * tracking. The clone is intentional: the main history reflects
     * the accepted iterate, and Armijo's probes are throwaway.
     *
     * Sprint 9 hardening (Sprint 8c followup smell #1 closure):
     * the clone is a SHALLOW copy of the Map. That is sufficient
     * only because every value stored in `junctionHistory` is a
     * primitive number (`vold`). If a future stamp type ever stores
     * a richer shape (`{ vold, dvdt }`, tuples, etc.), the shallow
     * clone leaves Armijo probes able to mutate sub-objects visible
     * to the main loop. {@link snapshotJunctionHistory} centralises
     * the clone behind a named type alias so the constraint is hard
     * to miss at the mutation site.
     */
    const evalResidual = (xTrial) => {
        const historySandbox = snapshotJunctionHistory(junctionHistory);
        const { Y: Yt, rhs: rhst } = buildMNA(circuit, plan, {
            xPrev: xTrial,
            gmin: opts.gmin ?? 0,
            sourceScale: opts.sourceScale ?? 1,
            junctionHistory: historySandbox,
            enableDeltaVDamping: dampingEnabled,
            temperature: temperatureK,
        });
        return computeResidual(Yt, xTrial, rhst);
    };
    for (let iter = 1; iter <= opts.maxIterations; iter++) {
        const { Y, rhs } = buildMNA(circuit, plan, {
            xPrev: x,
            gmin: opts.gmin ?? 0,
            sourceScale: opts.sourceScale ?? 1,
            junctionHistory,
            enableDeltaVDamping: dampingEnabled,
            temperature: temperatureK,
        });
        // Sprint 8b: residual-based convergence check at the TOP of the
        // loop. `Y` and `rhs` are freshly built at the current iterate
        // `x`, so `residual = Y·x - rhs` evaluates `f(x)` — the Kirchhoff
        // current balance at each node. After LU solves `Y·x_new = rhs`
        // the residual is identically zero by construction, so the check
        // must happen BEFORE the solve (or equivalently, on the next
        // iter's fresh build — the pattern here).
        //
        // The "checked iterate" is `x` (= previous iter's `xNew`); the
        // solve below computes the next one. When we decide the previous
        // iterate was converged we return it immediately without a
        // further solve.
        const residualTop = computeResidual(Y, x, rhs);
        let residMax = 0;
        for (const i of plan.nodeIndexSet) {
            const r = residualTop[i] ?? 0;
            const abs = Math.abs(r);
            if (abs > residMax)
                residMax = abs;
        }
        finalResidualNorm = residMax;
        // Sprint 8c: feed every iter's residual into the stall detector
        // and promote Armijo once the window says progress has stalled.
        stallDetector.record(residMax);
        if (!armijoActive && armijoEnabled && iter > 3 && stallDetector.isStalled()) {
            armijoActive = true;
        }
        if (previousStepDelta !== null) {
            const converged = isConvergedReduced({
                x,
                residual: residualTop,
                deltaX: previousStepDelta,
                nodeIndexSet: plan.nodeIndexSet,
                tier,
            });
            if (converged) {
                return {
                    converged: true,
                    iterations: iter - 1,
                    x,
                    clampEvents,
                    finalResidualNorm,
                    armijoEvents,
                };
            }
        }
        const xNewRaw = sparseSolve(Y, rhs);
        iterations = iter;
        if (!isFiniteVector(xNewRaw)) {
            throw new ConvergenceError(`Newton-Raphson produced non-finite iterate at step ${String(iter)}`, { meta: { iterations: iter, gmin: opts.gmin ?? 0, sourceScale: opts.sourceScale ?? 1 } });
        }
        let xNew;
        let stepArmijoTriggered = false;
        // Sprint 9 (Sprint 8c followup smell #2 closure): whenever
        // Armijo engages at all — whether it accepts full α=1,
        // accepts a backtracked α<1, short-circuits at the trivial
        // fixed point, or exhausts and falls through to delta-V
        // damping — the next iteration's sign-flip damping heuristic
        // must NOT consult `previousDeltas` captured before Armijo
        // stepped in. That delta vector reflects the pre-Armijo
        // direction, which Armijo has just validated or overridden;
        // reusing it next iter misreads the oscillation signature.
        // `stepArmijoAttempted` captures this coarser invariant so
        // the reset at the end of the iteration is unambiguous.
        let stepArmijoAttempted = false;
        if (armijoActive) {
            stepArmijoAttempted = true;
            // Sprint 8c: Armijo backtracking on reduced-KCL residual.
            // The Newton direction is `p = xNewRaw - x`; Armijo probes
            // `x + α·p` at decreasing α until the sufficient-decrease
            // bound holds. When every backtrack fails, fall through to
            // the Sprint 8a delta-V damping path so the iter isn't
            // wasted.
            const p = new Float64Array(x.length);
            for (let i = 0; i < x.length; i++)
                p[i] = (xNewRaw[i] ?? 0) - (x[i] ?? 0);
            const result = armijoLineSearch(x, p, residualTop, evalResidual, plan.nodeIndexSet, armijoOptions);
            if (result.accepted && result.alpha > 0) {
                xNew = result.xNew;
                // Track non-full steps — they are the interesting case
                // for `/ultrareview` and regression tests. `α = 1`
                // accepts are full Newton and do not count.
                if (result.alpha < 1) {
                    armijoEvents += 1;
                    stepArmijoTriggered = true;
                }
            }
            else if (result.accepted && result.alpha === 0) {
                // Trivial fixed-point short-circuit from line-search.
                // Return the iterate as-is; the outer convergence check
                // will see no change and exit. If the iterate is a
                // physically-wrong trivial fixed point, the `DCSolver`
                // warning path surfaces that to the user.
                xNew = x;
            }
            else {
                // Armijo exhausted its backtracks — fall through to
                // delta-V damping on the RAW Newton step so we still
                // make forward progress.
                if (dampingEnabled && plan.junctionBearingIndices.size > 0) {
                    const damped = applyDeltaVDamping(xNewRaw, x, plan.junctionBearingIndices, deltaVLimit, previousDeltas);
                    xNew = damped.result;
                    if (damped.alpha < 1)
                        clampEvents += 1;
                    previousDeltas = damped.junctionDeltas;
                }
                else {
                    xNew = xNewRaw;
                }
            }
        }
        else if (dampingEnabled && plan.junctionBearingIndices.size > 0) {
            // Sprint 8a: trust-region step scaling keyed on junction-
            // bearing nets. Linear / MOSFET-only circuits leave the junction
            // set empty so the helper short-circuits and alpha = 1.
            // Sprint 8a.1: pass `previousDeltas` so the helper can skip
            // damping when Newton is climbing monotonically (sign-flip free).
            const damped = applyDeltaVDamping(xNewRaw, x, plan.junctionBearingIndices, deltaVLimit, previousDeltas);
            xNew = damped.result;
            if (damped.alpha < 1)
                clampEvents += 1;
            previousDeltas = damped.junctionDeltas;
        }
        else {
            xNew = xNewRaw;
        }
        // Sprint 8c + Sprint 9 (smell #2 closure): reset delta-V's
        // previousDeltas tracker whenever Armijo engaged at all this
        // iter, not only on α<1 accept. See commentary on
        // `stepArmijoAttempted` above — pre-Armijo deltas are
        // structurally stale once Armijo has entered the loop.
        // `stepArmijoTriggered` is kept for pre-Sprint-9 readers as a
        // strictly-narrower signal (α<1 accept specifically).
        void stepArmijoTriggered; // observed by diagnostics only
        if (stepArmijoAttempted) {
            previousDeltas = null;
        }
        // Record the accepted step so the next iter's top-of-loop
        // convergence check can consult it as deltaX.
        const stepDelta = new Float64Array(xNew.length);
        for (let i = 0; i < xNew.length; i++)
            stepDelta[i] = (xNew[i] ?? 0) - (x[i] ?? 0);
        previousStepDelta = stepDelta;
        x = xNew;
    }
    // One last check: build at the final iterate and evaluate residual.
    const { Y: Yf, rhs: rhsf } = buildMNA(circuit, plan, {
        xPrev: x,
        gmin: opts.gmin ?? 0,
        sourceScale: opts.sourceScale ?? 1,
        junctionHistory,
        enableDeltaVDamping: dampingEnabled,
        temperature: temperatureK,
    });
    const residualFinal = computeResidual(Yf, x, rhsf);
    let residMaxFinal = 0;
    for (const i of plan.nodeIndexSet) {
        const r = residualFinal[i] ?? 0;
        const abs = Math.abs(r);
        if (abs > residMaxFinal)
            residMaxFinal = abs;
    }
    finalResidualNorm = residMaxFinal;
    if (previousStepDelta !== null) {
        const converged = isConvergedReduced({
            x,
            residual: residualFinal,
            deltaX: previousStepDelta,
            nodeIndexSet: plan.nodeIndexSet,
            tier,
        });
        if (converged) {
            return { converged: true, iterations, x, clampEvents, finalResidualNorm, armijoEvents };
        }
    }
    return { converged: false, iterations, x, clampEvents, finalResidualNorm, armijoEvents };
}
//# sourceMappingURL=newton-raphson.js.map